import React from 'react';
import { CheckCircle, Loader2, Trash2, ArrowRight, FileText, Image } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

function getFileIcon(filename) {
  const ext = filename.split('.').pop().toLowerCase();
  if (['png', 'jpg', 'jpeg', 'webp'].includes(ext)) return <Image className="w-5 h-5 text-zinc-400" />;
  return <FileText className="w-5 h-5 text-zinc-400" />;
}

export function ProcessingResults({ results, onUploadMore, onGoToReview, onDeleteFile }) {
  if (!results) return null;

  const isInProgress = results.progressMessage && !results.files?.length;

  return (
    <Card>
      <CardContent className="pt-6 pb-6">
        {isInProgress ? (
          <div data-testid="processing-progress">
            <div className="flex items-center gap-3 mb-4">
              <Loader2 className="w-5 h-5 text-[#D94E18] animate-spin" />
              <h3 className="text-base font-semibold text-zinc-900">Processing...</h3>
            </div>
            <div className="flex items-center gap-2 mb-4">
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
                results.currentStep === 'extracting' ? 'bg-[#D94E18] text-white' :
                ['classifying','completed'].includes(results.currentStep) ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-400'
              }`}>
                {['classifying','completed'].includes(results.currentStep) ? <CheckCircle className="w-3.5 h-3.5" /> : <span className="w-3.5 h-3.5 flex items-center justify-center">1</span>}
                Extract
              </div>
              <div className="w-6 h-px bg-zinc-200" />
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
                results.currentStep === 'classifying' ? 'bg-[#D94E18] text-white' :
                results.currentStep === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-400'
              }`}>
                {results.currentStep === 'completed' ? <CheckCircle className="w-3.5 h-3.5" /> : <span className="w-3.5 h-3.5 flex items-center justify-center">2</span>}
                Classify
              </div>
            </div>
            <div className="w-full h-2 bg-zinc-100 rounded-full overflow-hidden mb-3">
              <div className="h-full bg-[#D94E18] rounded-full transition-all duration-700" style={{ width: `${results.progressPercent || 5}%` }} />
            </div>
            <p className="text-sm text-zinc-500">{results.progressMessage}</p>
            {results.transactionCount > 0 && results.currentStep === 'classifying' && (
              <p className="text-xs text-zinc-400 mt-1">{results.transactionCount} transactions found</p>
            )}
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-5">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              <h3 className="text-base font-semibold text-zinc-900">Upload Complete</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="p-3 bg-zinc-50 rounded-lg">
                <p className="text-xs text-zinc-500 mb-1">Transactions</p>
                <p className="text-xl font-semibold text-zinc-900">{results.totalTransactions}</p>
              </div>
              <div className="p-3 bg-red-50/60 rounded-lg">
                <p className="text-xs text-zinc-500 mb-1">Total Debits</p>
                <p className="text-base font-semibold text-red-600 font-mono">{formatCurrency(results.totalDebits)}</p>
              </div>
              <div className="p-3 bg-emerald-50/60 rounded-lg">
                <p className="text-xs text-zinc-500 mb-1">Total Credits</p>
                <p className="text-base font-semibold text-emerald-600 font-mono">{formatCurrency(results.totalCredits)}</p>
              </div>
              <div className="p-3 bg-zinc-50 rounded-lg">
                <p className="text-xs text-zinc-500 mb-1">Net Amount</p>
                <p className={`text-base font-semibold font-mono ${results.netAmount >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(results.netAmount)}</p>
              </div>
            </div>
            <div className="space-y-2 mb-5">
              <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Processed Files</p>
              {results.files.map((file) => (
                <div key={file.id} className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                  <div className="flex items-center gap-3 min-w-0">
                    {getFileIcon(file.filename)}
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-zinc-800 truncate">{file.filename}</p>
                      <p className="text-xs text-zinc-500">
                        {file.transactions} txns
                        {file.duplicates > 0 && <span className="text-amber-600"> ({file.duplicates} potential duplicates)</span>}
                        {' '}&middot; Debits {formatCurrency(file.debits)} &middot; Credits {formatCurrency(file.credits)}
                      </p>
                    </div>
                  </div>
                  {file.id && (
                    <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-red-500 hover:bg-red-50 flex-shrink-0" onClick={() => onDeleteFile(file)} data-testid={`delete-processed-${file.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={onUploadMore} className="flex-1" data-testid="upload-more-btn">Upload More</Button>
              <Button onClick={onGoToReview} className="flex-1 bg-[#D94E18] hover:bg-[#C2410C]" data-testid="go-to-review-btn">
                Review & Post to GL<ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
